import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './App.css';

const ScoreBoardTable = () => {
   const [scores, setScores] = useState([]);
   const [gameSelected, setGameSelected] = useState('');
   const [gameTitle, setGameTitle] = useState('');
   const [scoringType, setScoringType] = useState('');
   const [isRulerOfHill, setIsRulerOfHill] = useState(false);
   const [message, setMessage] = useState('Select a game to view scores');
   const [gameOptions, setGameOptions] = useState([]);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState('');

   // Enhanced formatting function with Ruler of the Hill support
   function formatOutput(score, type) {
      if (!score && score !== 0) return 'N/A';
      
      switch (type?.toLowerCase()) {
         case 'rulerofthehill':
            // For Ruler of the Hill, show as points with special indicator
            const points = parseFloat(score);
            if (isNaN(points)) return 'Invalid Score';
            return `${Math.round(points).toLocaleString()} 👑`;
            
         case 'time':
            const totalSeconds = parseFloat(score);
            if (isNaN(totalSeconds)) return 'Invalid Time';
            
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = Math.floor(totalSeconds % 60);
            const milliseconds = Math.round((totalSeconds % 1) * 1000);
            return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
            
         case 'distance':
            const distance = parseFloat(score);
            if (isNaN(distance)) return 'Invalid Distance';
            return `${distance.toFixed(2)} ft`;
            
         case 'points':
         case 'score':
         default:
            const regularPoints = parseFloat(score);
            if (isNaN(regularPoints)) return 'Invalid Score';
            return Math.round(regularPoints).toLocaleString();
      }
   }

   // Get user-friendly message based on scoring type
   function getScoringMessage(type, isRulerOfHillMode) {
      if (!type) return '';
      
      // Check if it's Ruler of the Hill
      if (isRulerOfHillMode || type.toLowerCase() === 'rulerofthehill') {
         return 'Ruler of the Hill Wins';
      }
      
      switch (type.toLowerCase()) {
         case 'time':
            return 'Fastest Time Wins';
         case 'distance':
            return 'Farthest Distance Wins';
         case 'points':
         case 'score':
         default:
            return 'Highest Score Wins';
      }
   }
     
   const fetchResults = async () => {
      if (!gameSelected) return;
      
      try {
        setLoading(true);
         setError('');
         
         const response = await fetch(`/topScores?game=${gameSelected}`);
         
         if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
         }
         
         const data = await response.json();
         console.log('Received scoreboard data:', data);
         
         setScores(data.players || []);
         setGameTitle(data.gameTitle || 'Unknown Game');
         setScoringType(data.scoringType || '');
         setIsRulerOfHill(data.isRulerOfHill || false);
         setMessage(getScoringMessage(data.scoringType, data.isRulerOfHill));
         
      } catch (error) {
         console.error('Error fetching scores:', error);
         setError('Failed to load scores. Please try again.');
         setScores([]);
      } finally {
         setLoading(false);
      }
   };

   // Load game options on component mount
   useEffect(() => {
      const loadGames = async () => {
         try {
            const response = await axios.get('/games/current');
            setGameOptions(response.data || []);
         } catch (err) {
            console.error('Failed to load games:', err);
            setGameOptions([]);
            setError('Failed to load games. Please refresh the page.');
         }
      };
      
      loadGames();
   }, []);

   // Fetch results when game changes and set up auto-refresh
   useEffect(() => {
      if (!gameSelected) {
         setScores([]);
         setGameTitle('');
         setScoringType('');
         setIsRulerOfHill(false);
         setMessage('Select a game to view scores');
         return;
      }
      
      // Initial fetch
      fetchResults();
      
      // Auto-refresh every 30 seconds
      const interval = setInterval(fetchResults, 30000);
      
      return () => clearInterval(interval);
   }, [gameSelected]);

   return (
   <div className="scoreboard-container">
        {/* Game Selection */}
        <div className="scoreboard-selection">
            <label className="form-label" htmlFor="gameSelect">Select a Game:</label>
            <select 
                className="form-select"
                name="gameSelected" 
                value={gameSelected} 
                onChange={(e) => setGameSelected(e.target.value)}
                disabled={loading}
            >
                <option value="">--Select a Game--</option>
                {gameOptions.map(game => (
                    <option key={game.shortName} value={game.shortName}>
                        {game.displayName}
                    </option>
                ))}
            </select>
        </div>
        
        {/* Game Title and Info */}
        <div className="scoreboard-header">
            <h1 className="game-title">{gameTitle || '\u00A0'}</h1>
        </div>
        

         {/* Loading State */}
         {loading && (
            <div className="margin-top-medium">
               <p>Loading scores...</p>
            </div>
         )}

         {/* Error State */}
         {error && (
            <div className="error-message margin-top-medium">
               {error}
            </div>
         )}

{/* Scores Table */}
{gameSelected && !loading && !error && (
    scores.length > 0 ? (
        <div className="scoreboard-table-wrapper">
            <table className="scoreboard-table margin-top-medium">
                <thead>
                    <tr>
                        {/* Dynamic headers based on game type */}
                        {isRulerOfHill ? (
                            <>
                                <th colSpan="3">👑 Current Ruler of the Hill 👑</th>
                            </>
                        ) : (
                            <>
                                <th>Rank</th>
                                <th>Player Name</th>
                                <th>Score</th>
                            </>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {isRulerOfHill ? (
                        // Ruler of the Hill - single row, centered name
                        <tr className="ruler-of-hill-row">
                            <td colSpan="3" style={{ textAlign: 'center', fontSize: '24px', padding: '20px' }}>
                                {scores[0].playerName || 'Unknown Player'}
                            </td>
                        </tr>
                    ) : (
                        // Regular scoring - show all rows with scores
                        scores.map((score, index) => (
                            <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{score.playerName || 'Unknown Player'}</td>
                                <td>{formatOutput(score.score, scoringType)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            
            {/* Scoring Message */}
            {message && (
                <div className="scoreboard-footer">
                    <p className="text-center"><strong>{message}</strong></p>
                </div>
            )}
        </div>
    ) : (
        <div className="margin-top-medium">
            <p>No scores recorded yet for this game.</p>
            {isRulerOfHill ? (
                <p>Be the first to claim the crown! 👑</p>
            ) : (
                <p>Be the first to play and set a high score!</p>
            )}
        </div>
    )
         )}
      {/* Auto-refresh indicator */}
         {gameSelected && (
            <div className="margin-top-small">
               <p style={{ fontSize: '14px', opacity: 0.8 }}>
                  Scores refresh automatically every 30 seconds
               </p>
            </div>
         )}
      
      </div>
   );
};

export default ScoreBoardTable;

